import i18n from "i18next";
/**
 * In-app notifications (Olivia, 10 Oct, design-handoffs/2026-10-10-sharing-notifications):
 * where a click goes, rows for one document merged, and which arrivals toast.
 */
import type { AppNotification } from "@/lib/api";

/** Within this, notifications of one kind for one document are one row. */
export const MERGE_WINDOW_MS = 10 * 60 * 1000;

export interface NotificationRow {
  /** The newest of the merged notifications. */
  head: AppNotification;
  /** All of them, newest first (one when nothing merged). */
  items: AppNotification[];
  unread: boolean;
}

/** The document a notification is about, from its link. */
export function documentOf(n: Pick<AppNotification, "link">): string | null {
  const m = /^\/(?:d|document)\/([0-9a-fA-F-]{8,})/.exec(n.link ?? "");
  return m ? m[1] : null;
}

/**
 * Where a click goes. Older notifications link to /document/:id (a redirect);
 * they go straight to /d/:id. Access removed goes nowhere: the row says who.
 */
export function destination(n: AppNotification): string | null {
  if (n.type === "access_removed" || !n.link) return null;
  return n.link.replace(/^\/document\//, "/d/");
}

/** Rows: one kind, one document, within ten minutes of the newest, merge. Newest first. */
export function mergeRows(list: readonly AppNotification[]): NotificationRow[] {
  const sorted = [...list].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  const rows: NotificationRow[] = [];
  for (const n of sorted) {
    const doc = documentOf(n);
    const row = doc
      ? rows.find((r) => r.head.type === n.type && documentOf(r.head) === doc && Date.parse(r.head.createdAt) - Date.parse(n.createdAt) <= MERGE_WINDOW_MS)
      : undefined;
    if (row) {
      row.items.push(n);
      row.unread ||= !n.isRead;
    } else rows.push({ head: n, items: [n], unread: !n.isRead });
  }
  return rows;
}

/** A merged row's title: the newest, and how many more. */
export function rowTitle(r: NotificationRow): string {
  const more = r.items.length - 1;
  return more > 0 ? i18n.t("shell:notifications.andMore", { title: r.head.title, count: more }) : r.head.title;
}

/**
 * The arrivals to toast: unread, newer than the last one seen, and not access
 * removed (no toast for that: the document turns read-only instead).
 */
export function toToast(list: readonly AppNotification[], seenUntil: number): AppNotification[] {
  return list
    .filter((n) => !n.isRead && Date.parse(n.createdAt) > seenUntil && n.type !== "access_removed")
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
}

/** "just now", "12 min ago", "3 h ago", "Tue", "3 Oct". */
export function whenShort(iso: string, now: Date = new Date()): string {
  const t = new Date(iso);
  const min = Math.round((now.getTime() - t.getTime()) / 60000);
  const lang = i18n.language ?? "en";
  const locale = lang.startsWith("fr") ? "fr-FR" : lang.startsWith("es") ? "es-ES" : "en-GB";
  if (min < 1) return i18n.t("shell:notifications.justNow");
  if (min < 60) return i18n.t("shell:notifications.min", { n: min });
  if (min < 24 * 60 && t.getDate() === now.getDate()) return i18n.t("shell:notifications.h", { n: Math.round(min / 60) });
  if (min < 7 * 24 * 60) return t.toLocaleDateString(locale, { weekday: "short" });
  return t.toLocaleDateString(locale, { day: "numeric", month: "short" });
}
